import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
    UsageCostRequestParams,
    useAnalyticsApi,
    useUsageAndCostLabelsMap,
} from '@/hooks'
import { getColumns } from '@/components/data/UsageAndCostTableContainer/getColumns'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { useTranslation } from 'react-i18next'
import { processUsageCostFilters } from '../utils/processUsageCostFilters'
import { extractColumnIds } from '@/features/Analytics/components/UsageAndCostExportReportDropdown/extractColumnIds'

export const useExportUsageCostReport = (reportId: string) => {
    const addAlert = useToastStore((state) => state.addAlert)
    const { t } = useTranslation('translations')
    const { useGetReport } = useReportsApi()
    const { data: report, refetch: refetchReport } = useGetReport(reportId, {
        enabled: false,
    })

    const triggered = useRef(false)

    const filters = report ? processUsageCostFilters(report.filters) : undefined
    const { useUsageCostQuery } = useAnalyticsApi()

    const {
        data: usageCost,
        refetch: refetchUsageCost,
        downloadXlsx,
    } = useUsageCostQuery(filters as UsageCostRequestParams, {
        enabled: !!report && triggered.current,
    })

    const labelsMap = useUsageAndCostLabelsMap(
        filters ?? ({} as UsageCostRequestParams),
        true,
        usageCost
    )

    const columnFields = useMemo(() => {
        const columns = getColumns(labelsMap, report?.fields ?? [])

        return extractColumnIds(columns).map((id) => id.replace(/_/g, '.'))
    }, [report?.fields, labelsMap])

    const downloadData = useCallback(() => {
        downloadXlsx(columnFields).catch(() => {
            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
        })
    }, [columnFields, addAlert, t, downloadXlsx])

    useEffect(() => {
        if (triggered.current && report && usageCost) {
            triggered.current = false
            downloadData()
        }
    }, [downloadData, usageCost, report, triggered])

    const exportUsageCostReport = () => {
        triggered.current = true

        if (!report) {
            void refetchReport()
            return
        }

        if (!usageCost) {
            void refetchUsageCost()
            return
        }

        triggered.current = false
        downloadData()
    }

    return {
        exportUsageCostReport,
    }
}
